import React, { useState } from 'react';
import { 
  Container, 
  CssBaseline, 
  ThemeProvider,
  Box,
  AppBar,
  Toolbar,
  Typography,
  Button,
  Snackbar,
  Alert,
  Tabs,
  Tab
} from '@mui/material';
import { ArrowBack as ArrowBackIcon, Info as InfoIcon } from '@mui/icons-material';
import { Task, ProjectInfo } from './types';
import { Project } from './components/ProjectList';
import RAPIDRolesDisplay from './components/RAPIDRolesDisplay';
import RASCIRolesDisplay from './components/RASCIRolesDisplay';
import ProjectInfoComponent from './components/ProjectInfo';
import DecisionMatrix from './components/DecisionMatrix';
import RASCIMatrix from './components/RASCIMatrix';
import ProjectList from './components/ProjectList';
import README from './components/README';
import DarkModeToggle from './components/DarkModeToggle';
import useLocalStorage from './hooks/useLocalStorage';
import { lightTheme, darkTheme } from './theme/theme';


// Sample initial data based on your Excel template
const initialTasks: Task[] = [
  {
    id: '1',
    workArea: 'Budget Management',
    taskDescription: 'Agree budget',
    assignments: {
      'P1': 'D',
      'P2': 'A',
      'P5': 'I'
    }
  },
  {
    id: '2',
    workArea: 'Project Planning',
    taskDescription: 'Determine the scope of the project',
    assignments: {
      'P1': 'D',
      'P4': 'R',
      'P6': 'A',
      'P9': 'A'
    }
  },
  {
    id: '3',
    workArea: 'Resource Planning',
    taskDescription: 'Outsource or insource?',
    assignments: {
      'P1': 'D',
      'P2': 'I',
      'P4': 'A',
      'P5': 'I/P',
      'P6': 'P',
      'P7': 'R/P',
      'P9': 'I'
    }
  },
  {
    id: '4',
    workArea: 'Deliverables',
    taskDescription: 'Produce deliverable A',
    assignments: {}
  },
  {
    id: '5',
    workArea: 'Deliverables',
    taskDescription: 'Produce deliverable B',
    assignments: {}
  }
];

const initialProjectInfo: ProjectInfo = {
  date: new Date().toLocaleDateString(),
  projectSummary: 'RAPID Decision Making Matrix Implementation'
};

// Sample projects
const initialProjects: Project[] = [
  {
    id: 'project-1',
    name: 'Website Redesign Project',
    description: 'Complete redesign of company website with new features and improved UX',
    date: '2024-01-15',
    lastModified: '2024-01-20'
  },
  {
    id: 'project-2',
    name: 'Mobile App Development',
    description: 'Development of a new mobile application for customer engagement',
    date: '2024-02-01',
    lastModified: '2024-02-10'
  }
];

// Project data storage
interface ProjectData {
  tasks: Task[];
  projectInfo: ProjectInfo;
  peopleConfig?: {
    people: any[];
    personNames: { [key: string]: string };
  };
}

function App() {
  const [currentView, setCurrentView] = useState<'index' | 'project'>('index');
  const [selectedProjectId, setSelectedProjectId] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState(0);
  const [isDarkMode, setIsDarkMode] = useLocalStorage<boolean>('rapid-dark-mode', true);
  const [projects, setProjects] = useLocalStorage<Project[]>('rapid-projects', initialProjects);
  const [projectData, setProjectData] = useLocalStorage<{ [key: string]: ProjectData }>('rapid-project-data', {
    'project-1': {
      tasks: initialTasks,
      projectInfo: initialProjectInfo
    },
    'project-2': {
      tasks: [],
      projectInfo: {
        date: new Date().toLocaleDateString(),
        projectSummary: 'Mobile App Development - RAPID Matrix'
      }
    }
  });
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' | 'info' }>({
    open: false,
    message: '',
    severity: 'info'
  });

  const handleSelectProject = (projectId: string) => {
    // Check if project has data, if not, initialize with default task
    if (!projectData[projectId]) {
      const project = projects.find(p => p.id === projectId);
      const defaultTask: Task = {
        id: `task-${Date.now()}`,
        workArea: '',
        taskDescription: '',
        assignments: {}
      };

      setProjectData(prev => ({
        ...prev,
        [projectId]: {
          tasks: [defaultTask],
          projectInfo: {
            date: new Date().toLocaleDateString(),
            projectSummary: project ? `${project.name} - ${project.matrixType || 'RAPID'} Matrix` : 'New Project'
          }
        }
      }));
    } else if (!projectData[projectId].tasks || projectData[projectId].tasks.length === 0) {
      // If project exists but has no tasks, add a default task
      const defaultTask: Task = {
        id: `task-${Date.now()}`,
        workArea: '',
        taskDescription: '',
        assignments: {}
      };

      setProjectData(prev => ({
        ...prev,
        [projectId]: {
          ...prev[projectId],
          tasks: [defaultTask]
        }
      }));
    }

    setSelectedProjectId(projectId);
    setCurrentView('project');
  };

  const handleBackToIndex = () => {
    setCurrentView('index');
    setSelectedProjectId(null);
  };

  const handleTasksChange = (tasks: Task[]) => {
    if (selectedProjectId) {
      setProjectData(prev => ({
        ...prev,
        [selectedProjectId]: {
          ...prev[selectedProjectId],
          tasks
        }
      }));
      setSnackbar({ open: true, message: 'Tasks updated successfully', severity: 'success' });
    }
  };

  const handleProjectInfoChange = (projectInfo: ProjectInfo) => {
    if (selectedProjectId) {
      setProjectData(prev => ({
        ...prev,
        [selectedProjectId]: {
          ...prev[selectedProjectId],
          projectInfo
        }
      }));
      setSnackbar({ open: true, message: 'Project info updated successfully', severity: 'success' });
    }
  };

  const handlePeopleConfigChange = (people: any[], personNames: { [key: string]: string }) => {
    if (selectedProjectId) {
      setProjectData(prev => ({
        ...prev,
        [selectedProjectId]: {
          ...prev[selectedProjectId],
          peopleConfig: { people, personNames }
        }
      }));
    }
  };

  const showSnackbar = (message: string, severity: 'success' | 'error' | 'info' = 'info') => {
    setSnackbar({ open: true, message, severity });
  };

  const handleCreateProjectWithData = (project: Project, projectDataForProject: ProjectData) => {
    setProjects(prev => [...prev, project]);
    setProjectData(prev => ({
      ...prev,
      [project.id]: projectDataForProject
    }));
    setSnackbar({ open: true, message: 'Project created successfully', severity: 'success' });
  };

  const handleCloseSnackbar = () => {
    setSnackbar(prev => ({ ...prev, open: false }));
  };

  const getCurrentProject = () => {
    return projects.find(p => p.id === selectedProjectId);
  };

  const getCurrentProjectData = () => {
    return selectedProjectId ? projectData[selectedProjectId] : null;
  };

  const handleDarkModeToggle = () => {
    setIsDarkMode(!isDarkMode);
  };

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
  };

  return (
    <ThemeProvider theme={isDarkMode ? darkTheme : lightTheme}>
      <CssBaseline />
      <Box sx={{ minHeight: '100vh', display: 'flex', flexDirection: 'column' }}>
        {/* Combined Header with Logo, Title, and Dark Mode Toggle */}
        <AppBar position="static">
          <Toolbar sx={{
            backgroundColor: isDarkMode ? '#1f2937' : '#2c3e50',
            py: 1
          }}>
            {/* Logo on the left */}
            <Box sx={{ display: 'flex', alignItems: 'center', minWidth: '200px' }}>
              <img
                src="/logo.webp"
                alt="IKUTU - Sustainable Energy Solutions"
                style={{
                  height: '50px',
                  objectFit: 'contain'
                }}
              />
            </Box>

            {/* Title in the center */}
            <Box sx={{ flexGrow: 1, textAlign: 'center' }}>
              {currentView === 'project' && (
                <Button
                  startIcon={<ArrowBackIcon />}
                  onClick={handleBackToIndex}
                  sx={{ color: 'white', mr: 2, position: 'absolute', left: '220px' }}
                >
                  Back to Projects
                </Button>
              )}
              <Typography variant="h5" component="div" sx={{ fontWeight: 'bold', color: 'white' }}>
                RAPID & RASCI Matrix
                {currentView === 'project' && getCurrentProject() && (
                  <span style={{ marginLeft: '10px', opacity: 0.8, fontSize: '0.9em' }}>
                    - {getCurrentProject()?.name}
                  </span>
                )}
              </Typography>
            </Box>

            {/* Dark Mode Toggle on the right */}
            <Box sx={{ minWidth: '200px', display: 'flex', justifyContent: 'flex-end' }}>
              <DarkModeToggle isDarkMode={isDarkMode} onToggle={handleDarkModeToggle} />
            </Box>
          </Toolbar>
        </AppBar>

        <Container maxWidth="xl" sx={{ mt: 0, mb: 4, flexGrow: 1, pt: 2 }}>
          {currentView === 'index' ? (
            <Box>
              <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
                <Tabs value={activeTab} onChange={handleTabChange} aria-label="main navigation tabs">
                  <Tab label="Projects" />
                  <Tab label="README" icon={<InfoIcon />} iconPosition="start" />
                </Tabs>
              </Box>
              
              {activeTab === 0 && (
                <ProjectList 
                  projects={projects}
                  onProjectsChange={setProjects}
                  onSelectProject={handleSelectProject}
                  onCreateProjectWithData={handleCreateProjectWithData}
                />
              )}
              
              {activeTab === 1 && <README />}
            </Box>
          ) : (
            getCurrentProjectData() && (
              <Box sx={{ flexGrow: 1 }}>
                <Box sx={{ mb: 3 }}>
                  {getCurrentProject()?.matrixType === 'RASCI' ? (
                    <RASCIRolesDisplay />
                  ) : (
                    <RAPIDRolesDisplay />
                  )}
                </Box>
                
                <Box sx={{ mb: 3 }}>
                  <ProjectInfoComponent 
                    projectInfo={getCurrentProjectData()!.projectInfo}
                    onProjectInfoChange={handleProjectInfoChange}
                  />
                </Box>
                
                <Box>
                  {getCurrentProject()?.matrixType === 'RASCI' ? (
                    <RASCIMatrix
                      tasks={getCurrentProjectData()!.tasks}
                      onTasksChange={handleTasksChange}
                      projectName={getCurrentProject()?.name || 'Untitled Project'}
                      showSnackbar={showSnackbar}
                      peopleConfig={getCurrentProjectData()!.peopleConfig}
                      onPeopleConfigChange={handlePeopleConfigChange}
                    />
                  ) : (
                    <DecisionMatrix
                      tasks={getCurrentProjectData()!.tasks}
                      onTasksChange={handleTasksChange}
                      projectName={getCurrentProject()?.name || 'Untitled Project'}
                      showSnackbar={showSnackbar}
                      peopleConfig={getCurrentProjectData()!.peopleConfig}
                      onPeopleConfigChange={handlePeopleConfigChange}
                    />
                  )}
                </Box>
              </Box>
            )
          )}
        </Container>
        
        {/* Footer */}
        <Box sx={{ 
          mt: 'auto',
          py: 3, 
          backgroundColor: isDarkMode ? '#1f2937' : '#2c3e50', 
          color: 'white',
          textAlign: 'center',
          borderTop: isDarkMode ? '1px solid #374151' : '1px solid #34495e'
        }}>
          <Typography variant="body2">
            © 2025 IKUTU - Sustainable Energy Solutions
          </Typography>
        </Box>
      </Box>
      
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </ThemeProvider>
  );
}

export default App;
